// AGC: UpgradeModal - Modal shown when user hits a feature limit
// AGC: Prompts user to upgrade to unlock more features

import React from 'react'
import { 
  X, 
  Zap, 
  Gift, 
  Star, 
  Crown,
  ArrowRight,
  Check,
  AlertTriangle,
} from 'lucide-react'
import { useSubscription } from '../../context/SubscriptionContext'
import { useTranslation } from '../../hooks/useTranslation'
import {
  type SubscriptionTier,
  type LimitedFeature,
  TIER_DISPLAY_INFO,
  getNextTier,
  UPGRADE_TRIGGERS,
} from '../../types/subscription'

interface UpgradeModalProps {
  onClose?: () => void
  onUpgrade?: (tier: SubscriptionTier) => void
}

/**
 * AGC: Feature labels for display - using translations
 */
const getFeatureLabels = (t: (key: string) => string): Record<LimitedFeature, { name: string; icon: string }> => ({
  tasks: { name: t('subscription.usage.tasks') || 'Tarefas', icon: '✓' },
  fields: { name: t('subscription.usage.fields') || 'Talhões', icon: '🗺️' },
  voiceCommands: { name: t('subscription.usage.voiceCommands') || 'Comandos de Voz', icon: '🎤' },
  whatsAppAudio: { name: t('subscription.usage.whatsAppAudio') || 'Áudios WhatsApp', icon: '💬' },
  hectares: { name: t('subscription.usage.hectares') || 'Hectares', icon: '📐' },
  teamMembers: { name: t('subscription.usage.teamMembers') || 'Membros da Equipe', icon: '👥' },
  hectareCalculator: { name: t('subscription.usage.hectareCalculator') || 'Calculadora de Hectares', icon: '📏' },
})

/**
 * AGC: Icon component for tiers
 */
const TierIcon: React.FC<{ tier: SubscriptionTier; className?: string }> = ({ tier, className = 'w-6 h-6' }) => {
  switch (tier) {
    case 'free':
      return <Gift className={className} />
    case 'pro':
      return <Star className={className} />
    case 'premium':
      return <Crown className={className} />
  }
}

/**
 * AGC: Upgrade Modal Component
 */
export const UpgradeModal: React.FC<UpgradeModalProps> = ({
  onClose,
  onUpgrade,
}) => {
  const { t } = useTranslation()
  const { 
    showUpgradeModal, 
    upgradeModalFeature, 
    closeUpgradeModal,
    tier: currentTier,
    checkFeatureLimit,
    updateTier,
    isLoading,
    tierConfigs,
  } = useSubscription()

  // AGC: Get feature labels with translations
  const featureLabels = getFeatureLabels(t)

  // AGC: Don't render if modal is closed
  if (!showUpgradeModal || !upgradeModalFeature) {
    return null
  }

  const handleClose = () => {
    closeUpgradeModal()
    onClose?.()
  }

  const handleUpgrade = async (targetTier: SubscriptionTier) => {
    try {
      if (onUpgrade) {
        onUpgrade(targetTier)
      } else {
        await updateTier(targetTier)
      }
      closeUpgradeModal()
    } catch (error) {
      console.error('Error upgrading:', error)
    }
  }

  const limitCheck = checkFeatureLimit(upgradeModalFeature)
  const featureInfo = featureLabels[upgradeModalFeature]
  const nextTier = getNextTier(currentTier)
  const trigger = UPGRADE_TRIGGERS.find(t => t.feature === upgradeModalFeature)
  
  // AGC: Get limits from dynamic tier configs (Firestore)
  const currentLimits = tierConfigs[currentTier]?.limits
  const nextTierLimits = nextTier ? tierConfigs[nextTier]?.limits : null
  const nextTierDisplay = nextTier ? TIER_DISPLAY_INFO[nextTier] : null
  const nextTierPricing = nextTier ? { monthly: tierConfigs[nextTier]?.price?.monthly ?? 0, annual: tierConfigs[nextTier]?.price?.annual ?? 0 } : null

  // AGC: Map feature to limit field
  const limitFieldMap: Record<LimitedFeature, keyof typeof currentLimits> = {
    tasks: 'maxTasksPerMonth',
    fields: 'maxFields',
    voiceCommands: 'maxVoiceCommandsPerMonth',
    whatsAppAudio: 'maxWhatsAppAudioPerMonth',
    hectares: 'maxHectares',
    teamMembers: 'maxTeamMembers',
    hectareCalculator: 'hectareCalculatorUsesPerMonth',
  }

  const limitField = limitFieldMap[upgradeModalFeature]
  const currentLimit = currentLimits[limitField]
  const nextLimit = nextTierLimits ? nextTierLimits[limitField] : null

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200">
        {/* AGC: Header */}
        <div className="relative p-6 bg-gradient-to-br from-amber-500 to-orange-500 text-white">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/30 active:ring-4 active:ring-[var(--ring-click-primary)] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-white/20 rounded-xl">
              <AlertTriangle className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-xl font-bold">
                {t('subscription.limitReached') || 'Limite Atingido'}
              </h2>
              <p className="text-white/80 text-sm">
                {featureInfo.icon} {featureInfo.name}
              </p>
            </div>
          </div>
          
          <p className="text-white/90">
            {trigger?.message
              .replace('{current}', String(limitCheck.current))
              .replace('{limit}', String(limitCheck.limit))
              .replace('{tier}', TIER_DISPLAY_INFO[currentTier].name) 
              || `Você atingiu o limite de ${limitCheck.limit} ${featureInfo.name.toLowerCase()}`
            }
          </p>
        </div>

        {/* AGC: Content */}
        <div className="p-6">
          {/* AGC: Current vs Next tier comparison */}
          {nextTier && nextTierDisplay && nextTierPricing && (
            <>
              <div className="flex items-center justify-between mb-6">
                {/* Current tier */}
                <div className="text-center">
                  <div className={`p-3 rounded-xl ${TIER_DISPLAY_INFO[currentTier].bgColor} mx-auto w-fit mb-2`}>
                    <TierIcon tier={currentTier} className={`w-6 h-6 ${TIER_DISPLAY_INFO[currentTier].color}`} />
                  </div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {TIER_DISPLAY_INFO[currentTier].name}
                  </p>
                  <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                    {currentLimit === null ? '∞' : currentLimit}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {featureInfo.name}
                  </p>
                </div>

                {/* Arrow */}
                <ArrowRight className="w-6 h-6 text-gray-400" />

                {/* Next tier */}
                <div className="text-center">
                  <div className={`p-3 rounded-xl ${nextTierDisplay.bgColor} mx-auto w-fit mb-2`}>
                    <TierIcon tier={nextTier} className={`w-6 h-6 ${nextTierDisplay.color}`} />
                  </div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {nextTierDisplay.name}
                  </p>
                  <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                    {nextLimit === null ? '∞' : nextLimit}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {featureInfo.name}
                  </p>
                </div>
              </div>

              {/* AGC: Key benefits */}
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 mb-6">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  {t('subscription.upgradeIncludes') || 'O upgrade inclui:'}
                </p>
                <ul className="space-y-2">
                  {nextLimit !== currentLimit && (
                    <li className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <Check className="w-4 h-4 text-emerald-500" />
                      {nextLimit === null 
                        ? `${featureInfo.name} ilimitados`
                        : `${(nextLimit as number) - (currentLimit as number || 0)}x mais ${featureInfo.name.toLowerCase()}`
                      }
                    </li>
                  )}
                  {nextTier === 'pro' && (
                    <>
                      <li className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <Check className="w-4 h-4 text-emerald-500" />
                        Comandos de voz offline
                      </li>
                      <li className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <Check className="w-4 h-4 text-emerald-500" />
                        Integração WhatsApp
                      </li>
                    </>
                  )}
                  {nextTier === 'premium' && (
                    <>
                      <li className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <Check className="w-4 h-4 text-emerald-500" />
                        Recursos ilimitados
                      </li>
                      <li className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <Check className="w-4 h-4 text-emerald-500" />
                        Consultor Humano
                      </li>
                    </>
                  )}
                </ul>
              </div>

              {/* AGC: Price */}
              <div className="text-center mb-6">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('subscription.startingAt') || 'A partir de'}
                </p>
                <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                  R$ {nextTierPricing.monthly}
                  <span className="text-base font-normal text-gray-500">/mês</span>
                </p>
              </div>

              {/* AGC: CTA Buttons */}
              <div className="space-y-3">
                <button
                  onClick={() => handleUpgrade(nextTier)}
                  disabled={isLoading}
                  className={`
                    w-full py-3 px-4 rounded-xl font-semibold transition-all
                    flex items-center justify-center gap-2
                    active:ring-4 active:ring-[var(--ring-click-primary)] active:shadow-lg
                    ${nextTier === 'premium'
                      ? 'bg-gradient-to-r from-yellow-500 to-amber-500 text-white hover:from-yellow-600 hover:to-amber-600 shadow-lg shadow-yellow-500/25'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                    }
                    disabled:opacity-50 disabled:cursor-not-allowed
                  `}
                >
                  <Zap className="w-5 h-5" />
                  {isLoading 
                    ? (t('common.loading') || 'Carregando...')
                    : `Fazer Upgrade para ${nextTierDisplay.name}`
                  }
                </button>
                
                <button
                  onClick={handleClose}
                  className="w-full py-3 px-4 rounded-xl font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
                >
                  {t('common.maybeLater') || 'Talvez depois'}
                </button>

                {/* AGC: Promo code link */}
                <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-2">
                  <button
                    onClick={() => { handleClose(); /* AGC: User navigates to subscription page */ }}
                    className="text-emerald-600 dark:text-emerald-400 hover:underline"
                  >
                    {t('subscription.coupon.haveCode') || 'Tem um código promocional?'}
                  </button>
                </p>
              </div>
            </>
          )}

          {/* AGC: Already at highest tier */}
          {!nextTier && (
            <div className="text-center py-6">
              <Crown className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {t('subscription.alreadyPremium') || 'Você já está no plano Premium!'}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {t('subscription.premiumMessage') || 'Você tem acesso a todos os recursos ilimitados.'}
              </p>
              <button
                onClick={handleClose}
                className="px-6 py-3 bg-gray-100 dark:bg-slate-700 rounded-xl font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
              >
                {t('common.close') || 'Fechar'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default UpgradeModal

